import {
  MDBBtn,
  MDBCol,
  MDBContainer,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
  MDBRow,
} from "mdbreact";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AppContainer } from "../../../containers/App";
import { CampaignContainer } from "../../../containers/Campaign";
import { ErrorContainer } from "../../../containers/Error";
import { StampCardContainer } from "../../../containers/StampCard";
import { UserContainer } from "../../../containers/User";
import ClosedQuestion from "../../modules/questionnaire/closed-question/ClosedQuestion";
import classes from "./QuestionnairePage.module.scss";

/**
 * スタンプ達成者アンケート画面コンポーネント
 */
const QuestionnairePage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const history = useHistory();
  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const userContainer = UserContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();
  const stampCardContainer = StampCardContainer.useContainer();

  const user = userContainer.user;
  const userId = user.id || "";

  const campaign = campaignContainer.campaign;
  const questionnaire = campaign?.subContents.questionnaire;
  const questions = questionnaire?.contents.questions;

  const stampCard = stampCardContainer.stampCard;
  const answers = stampCard?.questionnaireStatus.answers;
  const isQuestionnaireAnsewered = stampCard?.questionnaireStatus.isAnswered;

  if (!answers && questions) {
    // 回答リストを初期化
    const qIds = questions.map((q) => q.qId);
    stampCardContainer.initQuestionnaireAnswers(qIds);
  }

  useEffect(() => {
    if (!user.token) {
      // 未ログインの場合、ログインページに遷移
      history.push("/login");
    }

    if (isQuestionnaireAnsewered && !isModalOpen) {
      // 回答済みの場合、スタンプカードページに遷移
      history.push(`/stamp-card`);
    }
  }, [isQuestionnaireAnsewered, history, user.token, isModalOpen]);

  /**
   * 回答が変化したときの処理
   * @param qId 問題ID
   * @param answer 回答番号
   * @param multiple 複数選択の可否
   */
  const handleAnswerChange = (
    qId: string,
    answer: number,
    multiple: boolean
  ) => {
    stampCardContainer.updateAnswers(qId, answer, multiple);
  };

  /**
   * 送信ボタン押下時の処理
   */
  const handleSubmitClick = async () => {
    if (!answers || !questions) {
      return;
    }

    // 回答を質問文の順番でソート
    answers.sort((a, b) => {
      const aIdx = questions.findIndex((q) => q.qId === a.qId);
      const bIdx = questions.findIndex((q) => q.qId === b.qId);
      return aIdx - bIdx;
    });

    // Firestoreへの登録
    try {
      appContainer.setLoading(true);
      // モーダルでメッセージを表示
      openModal();
      // 回答をセット
      await stampCardContainer.setAnswers();
    } catch (error) {
      errorContainer.setDefaultError(userId, error);
    } finally {
      appContainer.setLoading(false);
    }
  };

  /**
   * 「閉じる」ボタン押下時の処理
   */
  const handleCloseClick = () => {
    history.push(`/stamp-card`);
  };

  /**
   * 送信可能な状態か検証する
   */
  const validate = () => {
    if (!answers?.length || !questions) {
      return false;
    }

    const requires = questions.filter((q) => q.require);
    const isSubmittable = requires.every((r) => {
      // 必須項目の全てに回答が存在するかを確認
      const ans = answers.find((a) => a.qId === r.qId);
      return ans?.answers.length;
    });
    return isSubmittable;
  };

  /**
   * モーダルを展開する
   */
  const openModal = () => {
    setIsModalOpen(true);
  };

  /**
   * モーダルを閉じる
   */
  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <MDBContainer className="py-4">
      <div className={`text-center mb-3 ${classes.title}`}>
        {campaign?.subContents.questionnaire.contents.title}
      </div>
      <div className={`text-center mb-4 ${classes.leadMessage}`}>
        {campaign?.subContents.questionnaire.contents.description.map(
          (d, i) => {
            return <div key={i}>{d}</div>;
          }
        )}
      </div>
      <hr></hr>
      {questions
        ? questions.map((q, i) => {
            const existing = answers?.find((a) => a.qId === q.qId);
            const ans = !existing ? [] : existing.answers;
            return (
              <div key={`${q.qId}_${i}`} className="mb-4">
                <div className="w-100 d-flex justify-content-center">
                  <div className={classes.widthControl}>
                    <ClosedQuestion
                      questionNumber={i + 1}
                      questionText={q.question}
                      choices={q.choices}
                      answers={ans}
                      require={q.require}
                      multiple={q.multiple}
                      onChange={(ans) =>
                        handleAnswerChange(q.qId, ans, q.multiple)
                      }
                    ></ClosedQuestion>
                  </div>
                </div>
                <hr></hr>
              </div>
            );
          })
        : null}

      <MDBRow className={`${classes.buttonContainer} mb-3`}>
        <MDBCol size="12" className="text-center">
          {validate() ? (
            <div className={classes.successMessage}>
              ご回答ありがとうございます。
              <br />
              「回答」ボタンを押して回答を送信してください。
            </div>
          ) : (
            <div className={classes.alertMessage}>
              <MDBIcon className="mr-1" icon="exclamation-triangle" />
              未回答の質問があります。
            </div>
          )}
          <MDBBtn
            className={`${classes.submitButton} py-2`}
            color="green"
            onClick={handleSubmitClick}
            disabled={!validate()}
          >
            回答
          </MDBBtn>
        </MDBCol>
        <MDBCol size="12" className="text-center">
          <MDBBtn
            className={`${classes.closeButton} py-2`}
            color="grey"
            onClick={handleCloseClick}
          >
            あとで回答する
          </MDBBtn>
        </MDBCol>
      </MDBRow>

      <MDBModal isOpen={isModalOpen} toggle={closeModal} centered>
        <MDBModalHeader toggle={closeModal}>
          <span className={classes.modalTitle}>回答を送信しました</span>
        </MDBModalHeader>
        <MDBModalBody className={classes.modalBodyMessage}>
          {questionnaire?.contents.completeMessage.map((m, i) => {
            return (
              <React.Fragment key={`cpl-message-${i}`}>
                {m}
                <br />
              </React.Fragment>
            );
          })}
        </MDBModalBody>
        <MDBModalFooter className={classes.modalFooter}>
          <MDBBtn
            color="yellow"
            className={classes.returnButton}
            onClick={closeModal}
          >
            TOPに戻る
          </MDBBtn>
        </MDBModalFooter>
      </MDBModal>
    </MDBContainer>
  );
};

export default QuestionnairePage;
